import mixpanel, { trackUser } from '../../utilities/mixpanel';
import { PTOAge, standardizeISODate } from '../../utilities/date';
import { unwrapGraphQlList } from '../../utilities/graphql';
import { DASHBOARD_ROUTE } from '../../constants/routes';
import { FETCH_USER_SUCCESS } from '../modules/user';
import { FETCH_SYSTEM_OVERVIEW_SUCCESS } from '../modules/system';
import { FETCH_BILLING_SUCCESS } from '../modules/billing';
import { FETCH_HOURLY_PRODUCTION_FAILURE, FETCH_HOURLY_PRODUCTION_SUCCESS } from '../modules/hourlyProduction';

import { FETCH_HOURLY_BATTERY_FAILURE, FETCH_HOURLY_BATTERY_SUCCESS } from '../modules/hourlyBattery';
import { FAILED_DATA_RETRIEVAL } from '../../constants/mixpanelEventNames';

const failedPerformanceActionTypes = [FETCH_HOURLY_PRODUCTION_FAILURE, FETCH_HOURLY_BATTERY_FAILURE];
const succeededPerformanceActionTypes = [FETCH_HOURLY_PRODUCTION_SUCCESS, FETCH_HOURLY_BATTERY_SUCCESS];

const actionTypeToLocation = type => {
  switch (type) {
    case FETCH_HOURLY_PRODUCTION_FAILURE:
    case FETCH_HOURLY_PRODUCTION_SUCCESS:
      return 'Hourly Chart';
    case FETCH_HOURLY_BATTERY_FAILURE:
    case FETCH_HOURLY_BATTERY_SUCCESS:
    default:
      return 'Hourly Battery Chart';
  }
};

const actionTypeToGraphQLPath = type => {
  switch (type) {
    case FETCH_HOURLY_PRODUCTION_SUCCESS:
      return 'siteProductionHourly';
    case FETCH_HOURLY_BATTERY_SUCCESS:
    default:
      return 'batteryHourly';
  }
};

const formatDate = dateISO => standardizeISODate(dateISO);

const performanceLocation = type => {
  const path = window.location.pathname === DASHBOARD_ROUTE ? 'Dashboard' : 'Energy System';
  return `${actionTypeToLocation(type)} – ${path}`;
};

const recordAnalytics = store => next => action => {
  // for failed performance data
  if (failedPerformanceActionTypes.includes(action.type)) {
    mixpanel.track(FAILED_DATA_RETRIEVAL, {
      'Start Date': formatDate(action.startDate),
      'End Date': formatDate(action.endDate),
      Error: action.error,
      Location: performanceLocation(action.type),
    });
  }

  // for succeeded performance data call, but no available data
  if (succeededPerformanceActionTypes.includes(action.type)) {
    let siteData = null;
    if (action.payload) {
      siteData = unwrapGraphQlList(action.payload?.data?.site, `${actionTypeToGraphQLPath(action.type)}.edges`);
    } else if (action.productionHourly) {
      siteData = action.productionHourly;
    }

    if (siteData.length === 0) {
      mixpanel.track(FAILED_DATA_RETRIEVAL, {
        'Start Date': formatDate(action.payload.startDate),
        'End Date': formatDate(action.payload.endDate),
        Error: 'Empty site data array',
        Location: performanceLocation(action.type),
      });
    }
  }

  // for new user data, update salesrep email from oppty super property and user property
  if (action.type === FETCH_USER_SUCCESS) {
    trackUser(action.payload);
  }

  // for new system overview data, update PTO age super property and user property
  if (action.type === FETCH_SYSTEM_OVERVIEW_SUCCESS) {
    try {
      const userEmail = store.getState().user.get('email');
      const { ptoDate } = store.getState().user.get('contract');
      const ptoAge = PTOAge(ptoDate, action.payload[0].tzName);

      mixpanel.identify(userEmail);
      mixpanel.people.set({ 'PTO Age': `${ptoAge} days` });
      mixpanel.register({ 'PTO Age': `${ptoAge} days` });
    } catch (e) {
      mixpanel.register({ 'PTO Age': '' });
    }
  }

  // for new billing data, update current balance super property and user property
  if (action.type === FETCH_BILLING_SUCCESS) {
    try {
      const userEmail = store.getState().user.get('email');
      const { totalAmountDue } = action.payload.customer;

      mixpanel.identify(userEmail);
      mixpanel.people.set({ 'Current Balance': totalAmountDue });
      mixpanel.register({ 'Current Balance': totalAmountDue });
    } catch (e) {
      mixpanel.register({ 'Current Blanace': '' });
    }
  }

  return next(action);
};

export default recordAnalytics;
